import { Logger, select, selectAll } from "@fruugo/utilities";
import { ModalSettings, ModalSize, ModalType } from "./types";
import { JSX, ReactElement, ReactNode } from "jsx-dom";
import { RetailerInfoModalContent } from "./RetailerInfoModalContent";
import dialogPolyfill from "dialog-polyfill";
import { Icon } from "../Icon";
import { i18n } from "../../i18n";
import { sendViewContentEvent } from "../../analytics/google-analytics";
import { LocalPlusModalContent } from "./LocalPlusModalContent";
import { DutiesAndTaxesModalContent } from "./DutiesAndTaxesModalContent";
import {
  RefurbishedInfoModalContent,
  initRefurbishedReadMoreLists,
} from "./RefurbishedInfoModalContent";
import { DutiesInformationModalContent } from "./DutiesInformationModalContent";
import { NoAdditionalDutiesInformationModalContent } from "./NoAdditionalDutiesInformationModalContent";

const Log = new Logger(window.environment);

export const config = {
  openModalClass: ".js-open-modal",
  modalClass: ".js-modal",
  modalBodyClass: ".js-modal-body",
};

interface ModalProps {
  id: ModalType;
  children: ReactNode;
  settings?: ModalSettings;
  handleClose: () => void;
}

const defaultSettings: Partial<ModalSettings> = {
  allowClose: true,
};

const modalSizes: Record<ModalSize, string> = {
  // 560px: 528px of content plus 16px padding either side. w-full so form content doesn't shrink it below that
  sm: "tw:w-full tw:max-w-[35rem]",
  md: "tw:max-w-3xl",
  lg: "tw:max-w-4xl",
  xl: "tw:max-w-7xl",
};

export const Modal = ({ id, children, settings, handleClose }: ModalProps) => {
  // Merge the settings with defaults
  const finalSettings = { ...defaultSettings, ...settings };
  const { allowClose, ariaLabel, ariaDescribedby } = finalSettings;

  const positionClass = settings?.position
    ? `position-${settings?.position}`
    : "";

  const modalSizeClass = settings?.size
    ? modalSizes[settings?.size]
    : modalSizes["md"];

  return (
    <dialog
      class={`js-modal dialog tw:rounded-2xl ${positionClass} ${modalSizeClass}`}
      data-modal-type={id}
      data-testid={`${id}-modal`}
      aria-modal={true}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      aria-live="polite"
    >
      {allowClose && (
        <header className="tw:p-4 tw:flex tw:items-center tw:justify-end tw:pb-0">
          <button
            class="tw:p-1 tw:border-none tw:bg-fruugo-border-control tw:rounded-full tw:transition-all tw:transform tw:hover:rotate-90 tw:ease-in-out tw:duration-300 tw:hover:bg-fruugo-border-control/50 tw:focus:bg-fruugo-border-control/50 tw:focus:ring-1 tw:ring-fruugo-dark-grey tw:focus:rotate-90"
            onClick={handleClose}
          >
            <Icon name="close" className="tw:fill-fruugo-dark-grey" />
          </button>
        </header>
      )}
      <article class={config.modalBodyClass.replace(".", "")}>
        {children}
      </article>
    </dialog>
  );
};

// Some modals are created with JS openModal events
// Other modals are created by clicking on an element. "Trigger" refers to when modals are created this way
export function initModalTriggers() {
  const selector = selectAll(config.openModalClass);

  selector &&
    selector.forEach((element) =>
      element.addEventListener("click", () => handleModalTriggerClick(element))
    );
}

function handleModalTriggerClick(element: Element) {
  const type = getModalType(element as HTMLElement);

  if (type) {
    const template = getModalTemplate(type);
    const settings = getModalSettings(type);

    template && openModal(type, template, settings);
  }
}

export function getModalById(id: ModalType) {
  return select<HTMLDialogElement>(
    `${config.modalClass}[data-modal-type="${id}"]`
  );
}

export function openModal(
  id: ModalType,
  body: JSX.Element,
  settings?: ModalSettings
) {
  let modal = getModalById(id);

  const finalSettings = { ...defaultSettings, ...settings };
  const { allowClose } = finalSettings;

  const handleClose = () => {
    settings?.onClose && settings.onClose();
    closeModal();
  };

  if (!modal) {
    document.body.appendChild(
      <Modal id={id} settings={settings} handleClose={handleClose}>
        {body}
      </Modal>
    );
    modal = modal = getModalById(id);
  } else {
    updateModal(id, body, settings);
  }

  // Polyfill for older browsers like Safari 15.2, can probably remove in 2026 after checking browser support.
  if (modal && !modal.showModal) {
    dialogPolyfill.registerDialog(modal);
  }

  // Open the modal
  modal?.showModal();
  settings?.onOpen && settings?.onOpen();

  if (allowClose) {
    modal?.addEventListener("mousedown", (event) =>
      handleBackDropClick(event, handleClose)
    );
    document.removeEventListener("keydown", disableEscToClose);
  } else {
    modal?.removeEventListener("mousedown", (event) =>
      handleBackDropClick(event, handleClose)
    );
    document.addEventListener("keydown", disableEscToClose);
  }

  // Provide a nice method to update the modal without specifying the ID
  const updateCurrentModal = (
    body: ReactElement | HTMLCollection,
    settings?: ModalSettings
  ) => updateModal(id, body, settings);

  return [closeModal, updateCurrentModal];
}

function disableEscToClose(event: KeyboardEvent) {
  if (event.key === "Escape") event.preventDefault();
}

export function updateModal(
  id: ModalType,
  body: ReactElement | HTMLCollection,
  settings?: ModalSettings
) {
  body && updateModalBody(id, body);
  settings && updateModalSettings(id, settings);
}

export function updateModalSettings(id: ModalType, settings: ModalSettings) {
  const { ariaLabel, ariaDescribedby } = settings;

  const modal = getModalById(id);
  if (modal) {
    id && modal.setAttribute("data-testid", `${id}-modal`);
    ariaLabel && modal.setAttribute("aria-label", ariaLabel);
    ariaDescribedby && modal.setAttribute("aria-describedby", ariaDescribedby);
  }
}

export function updateModalBody(
  id: ModalType,
  children: ReactElement | HTMLCollection
) {
  const modal = getModalById(id);
  const body = modal?.querySelector(config.modalBodyClass);
  body && body.replaceChildren(<>{children}</>);
}

/**
 * This creates a bug where clicking on the scrollbar closes the modal, but it's the lesser evil for now.
 * chrome 134 is adding a closedby attribute which will handle this out of the box, please replace with that when it's stable
 * https://developer.chrome.com/blog/chrome-134-beta#dialog_light_dismiss
 */
function handleBackDropClick(event: Event, handleClose: () => void) {
  if (event.target === event.currentTarget) {
    handleClose();
  }
}

export function closeModal() {
  const modal = select<HTMLDialogElement>(`${config.modalClass}[open]`);
  if (!modal) return;

  if (modal) {
    document.removeEventListener("keydown", disableEscToClose);
    modal.close();
  }
}

function getModalType(element: HTMLElement): ModalType | null {
  const type = element.dataset.modalType;

  if (!type) {
    Log.warning(
      "[data-modal-type] attribute missing from selector when launching modal."
    );
    return null;
  }

  const modalType = mapModalType(type);

  if (!modalType) {
    Log.warning(
      `Unsupported modal type: ${type}. Please add this type and setup a template for it.`
    );
    return null;
  }

  return modalType;
}

function mapModalType(value: string): ModalType | undefined {
  if (Object.values(ModalType).includes(value as ModalType)) {
    return value as ModalType;
  }
  return undefined;
}

function getModalTemplate(type: ModalType): ReactElement | null {
  let template;

  switch (type) {
    case ModalType.RetailerInfo:
      template = <RetailerInfoModalContent />;
      break;
    case ModalType.LocalPlus:
      template = <LocalPlusModalContent />;
      break;
    case ModalType.DutiesAndTaxes:
      template = <DutiesAndTaxesModalContent />;
      break;
    case ModalType.DutiesInformation:
      template = <DutiesInformationModalContent />;
      break;
    case ModalType.NoAdditionalDutiesInformation:
      template = <NoAdditionalDutiesInformationModalContent />;
      break;
    case ModalType.RefurbishedInfo:
      template = <RefurbishedInfoModalContent />;
      break;
    default:
      template = null;
      break;
  }

  if (!template) {
    Log.warning(`Unable to get modal template for type: ${type}.`);
    return null;
  }

  return template;
}

function getModalSettings(type: ModalType): ModalSettings | undefined {
  let settings: ModalSettings | undefined;

  switch (type) {
    case ModalType.RetailerInfo:
      settings = {
        ariaLabel: i18n("modal.store-information-label"),
        ariaDescribedby: "store-information-aria-description",
        onOpen: () =>
          sendViewContentEvent(
            "retailer_info_modal",
            "Retailer Information Modal"
          ),
      };
      break;
    case ModalType.LocalPlus:
      settings = {
        onOpen: () =>
          sendViewContentEvent("local_plus_modal", "Local Plus Modal"),
      };
      break;
    case ModalType.DutiesAndTaxes:
      settings = {
        size: "lg",
        onOpen: () =>
          sendViewContentEvent(
            "duties_and_taxes_modal",
            "Duties and Taxes Modal"
          ),
      };
      break;
    case ModalType.DutiesInformation:
      settings = {
        onOpen: () =>
          sendViewContentEvent(
            "duties_information_modal",
            "Duties Information Modal"
          ),
      };
      break;
    case ModalType.NoAdditionalDutiesInformation:
      settings = {
        onOpen: () =>
          sendViewContentEvent(
            "no_additional_duties_information_modal",
            "No Additional Duties Information Modal"
          ),
      };
      break;
    case ModalType.RefurbishedInfo:
      settings = {
        size: "xl",
        onOpen: () => {
          sendViewContentEvent(
            "refurbished_info_modal",
            "Refurbished Information Modal"
          );

          initRefurbishedReadMoreLists();
        },
      };
      break;
  }

  return settings;
}
